import {
  customNotification,
  dismissNotification,
  successNotification,
  updateNotification,
} from '@sceneio/cms-notifications'
import {
  setAiActionsState,
  useEditorDispatch,
} from '@sceneio/content-store/lib/features/editor/editorSlice'
import { useCallback, useMemo } from 'react'
import { Notification } from '@sceneio/cms-notifications/lib/Notifications/Notification'
import { AI_NOTIFICATION_MESSAGES } from '@sceneio/cms-notifications/lib/notificationMessages'

export const useCustomNotifications = () => {
  const editorDispatch = useEditorDispatch()

  const undo = useCallback(() => {
    editorDispatch({
      type: 'LOCAL_HISTORY_UNDO',
    })
  }, [editorDispatch])

  const undoButton = useMemo(
    () => ({
      text: 'Undo',
      onClick: undo,
    }),
    [],
  )

  return useMemo(
    () => ({
      loadingNotification: {
        notify: (
          id,
          content,
          { autoClose }: { autoClose?: number | false } = {},
        ) => {
          customNotification(Notification, {
            id,
            options: {
              autoClose: autoClose ?? 30000,
              closeButton: false,
              data: {
                status: 'loading',
                content: content,
              },
            },
          })
        },
        update: (
          id,
          { status = 'success', content = 'Done!', allowUndo = true } = {},
        ) => {
          updateNotification(id, {
            autoClose: 5000,
            closeButton: true,
            data: {
              status,
              content,
              buttonsPlacement: 'top',
              ...(status === 'success' && {
                ...(allowUndo && {
                  buttons: [undoButton],
                }),
              }),
            },
          })
        },
        dismiss: (id) => {
          dismissNotification(id)
        },
      },
      aiLoadingNotification: {
        notify: (
          id,
          content,
          {
            visible = true,
            autoClose,
          }: { visible?: boolean; autoClose?: number | false } = {},
        ) => {
          customNotification(Notification, {
            id,
            options: {
              autoClose: autoClose ?? 30000,
              closeButton: false,
              className: !visible ? 'tw-hidden' : '',
              data: {
                content: AI_NOTIFICATION_MESSAGES[id]?.['loading'] || content,
                status: 'loading',
              },
            },
          })
        },
        update: (
          id,
          {
            status = 'success',
            content = 'Done!',
            allowMakeChanges = false,
            aiPromptUuid,
            aiAction,
            aiContext,
            visible = true,
          }: {
            status?: string
            allowMakeChanges?: boolean
            content?: string
            aiPromptUuid?: string
            aiAction?: string
            aiContext?: string
            visible?: boolean
          } = {},
        ) => {
          updateNotification(id, {
            autoClose: 5000,
            closeButton: true,
            className: !visible ? 'tw-hidden' : '',
            data: {
              content: AI_NOTIFICATION_MESSAGES[id]?.[status] || content,
              status,
              buttonsPlacement: 'top',
              ...(status === 'success' && {
                buttons: [
                  undoButton,
                  ...(allowMakeChanges
                    ? [
                        {
                          text: 'Make changes',
                          onClick: () =>
                            editorDispatch(
                              setAiActionsState({
                                show: true,
                                action: aiAction,
                                context: aiContext as any,
                                promptUuid: aiPromptUuid,
                              }),
                            ),
                        },
                      ]
                    : []),
                ],
              }),
            },
          })
        },
      },
      addBlockNotification: {
        notify: (blockTitle: string) => {
          successNotification({
            content: `${blockTitle} was added.`,
            options: {
              autoClose: 1000,
            },
          })
        },
      },
    }),
    [],
  )
}
